import type { SpecType } from '@local/lib';

import { Button, Icon, Popover } from '@salesforce/design-system-react';
import { useEffect, useRef, useState } from 'react';

import type { SpecDefinition } from '../qualityFormulaTypes';

import { IconCategory } from '../../../shared/types/Enum';

interface AddSpecMenuProps {
  availableSpecs: SpecDefinition[];
  children: React.ReactElement;
  onAdd: (specType: SpecType) => void;
  onRemove: (specType: SpecType) => void;
  selectedSpecs?: SpecDefinition[];
}

const noSelectedSpecs: SpecDefinition[] = [];

/** The compact navigator's add menu; outside interactions dismiss it. */
export const AddSpecMenu: React.FC<AddSpecMenuProps> = ({
  availableSpecs,
  children,
  onAdd,
  onRemove,
  selectedSpecs = noSelectedSpecs,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !menuRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointerDown);
    };
  }, [isOpen]);

  if (availableSpecs.length === 0 && selectedSpecs.length === 0) return children;

  return (
    <div ref={menuRef}>
      <Popover
        align="bottom left"
        body={
          <ul className="qfw-add-spec-list" role="menu">
            {selectedSpecs.map(spec => (
              <li className="qfw-add-spec-selected" key={spec.key} role="presentation">
                <span className="slds-truncate">{spec.label}</span>
                <span className="qfw-add-spec-selected-actions">
                  <Icon
                    category={IconCategory.Utility}
                    name="check"
                    size="xx-small"
                    style={{ fill: 'var(--slds-g-color-success-1)' }}
                    title="Added"
                  />
                  <Button
                    assistiveText={{ icon: `Remove ${spec.label}` }}
                    className="slds-button_icon-error"
                    iconCategory={IconCategory.Utility}
                    iconName="delete"
                    onClick={() => {
                      setIsOpen(false);
                      onRemove(spec.key);
                    }}
                    title={`Remove ${spec.label}`}
                    variant="icon"
                  />
                </span>
              </li>
            ))}
            {selectedSpecs.length > 0 && availableSpecs.length > 0 ? (
              <li className="qfw-add-spec-divider" role="separator" />
            ) : null}
            {availableSpecs.map(spec => (
              <li className="slds-dropdown__item" key={spec.key} role="presentation">
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- SLDS menu item action */}
                <a
                  aria-label={`Add ${spec.label}`}
                  href="#"
                  onClick={event => {
                    event.preventDefault();
                    setIsOpen(false);
                    onAdd(spec.key);
                  }}
                  role="menuitem"
                >
                  <span>{spec.label}</span>
                  <Icon category={IconCategory.Utility} name="add" size="xx-small" />
                </a>
              </li>
            ))}
          </ul>
        }
        className="qfw-add-spec-popover slds-popover_panel"
        hasNoCloseButton
        heading={selectedSpecs.length > 0 ? 'Quality Specs' : 'Available to Add'}
        isOpen={isOpen}
        onClick={() => {
          setIsOpen(open => !open);
        }}
        onRequestClose={() => {
          setIsOpen(false);
        }}
      >
        {children}
      </Popover>
    </div>
  );
};
